/** @jsxImportSource @emotion/react */
import { CSSProperties, FunctionComponent } from 'react';
import { css } from '@emotion/react';
import { hooks as commonHooks, types as commonTypes } from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import multiColumnStyles, { themeMultiColumns } from './MultiColumn.styles';

const CHILDREN_PLACEHOLDER_ID = 'column';

interface column extends CSSProperties {
    width?: string;
    padding?: string;
    border?: string;
}
interface IsRender {
    hideColumns?: number[];
    hideColumnsMessages: string[];
    widget?: true;
    hideWidgetMsg: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase {
    columns: column[];
    transformData?: boolean;
    functionToTransformData?: string;
    typographyClass?: string;
}
const MultiColumn: FunctionComponent<React.PropsWithChildren<commonTypes.WidgetInstanceProps>> = (
    props: commonTypes.WidgetInstanceProps
) => {
    const { Placeholder } = commonHooks.useAppContext();
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const { styles, columns, functionToTransformData, transformData } = widgetProperties || {};
    const i18n = commonHooks.useLocalization();
    const locale = i18n.getLocale();
    const { data } = props;
    const theme = data?.themeSelected || (props as commonTypes.WidgetInstanceProps).theme;

    let isRender: IsRender = {
        hideColumns: [],
        hideColumnsMessages: [],
        widget: true,
        hideWidgetMsg: '',
    };

    try {
        // eslint-disable-next-line no-new-func
        const transformFunction = Function(`${functionToTransformData}`)();
        const transformedProps = transformData && transformFunction(data);

        if (transformedProps) {
            isRender = { ...isRender, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    return (
        <>
            {isRender.widget ? (
                <div
                    className={`multi-column ${
                        widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''
                    }`}
                    css={css`
                        ${!theme ? multiColumnStyles : themeMultiColumns}
                        ${multiColumnStyles}${getTextSchemaProperties(
                            widgetProperties,
                            data?.colorSpace
                        )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                            widgetProperties
                        )}${styles || ''}
                    `}
                    data-testid="multi-column"
                >
                    {columns?.map((c: column, index: number) =>
                        !isRender.hideColumns?.includes(index) ? (
                            <div
                                style={c}
                                key={index}
                                className={`${
                                    locale === 'fr-CA' ? 'french' : ''
                                } column column--${index}`}
                            >
                                <Placeholder
                                    data={data}
                                    contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}--${index}`}
                                    parentWidgetInstanceId={widgetInstance.id}
                                />
                            </div>
                        ) : (
                            <p
                                className={`hide-msg-column column-${index}`}
                                key={index}
                                dangerouslySetInnerHTML={{
                                    __html: i18n.format(isRender?.hideColumnsMessages[index], data),
                                }}
                            />
                        )
                    )}
                </div>
            ) : (
                <p
                    className={`hide-msg-widget`}
                    dangerouslySetInnerHTML={{
                        __html: i18n.format(isRender?.hideWidgetMsg, data),
                    }}
                />
            )}
        </>
    );
};

export default MultiColumn;
